import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { serversApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import type { DeviceSpecDevice, EdgeMachine, SpecMachine } from '../../types'
import { useHoverTip } from './HoverTip'
import type { TipContent } from './HoverTip'
import { compareItems, labels, machinePlan, mergeEdgeMachine, pulledItems, tagResolver } from './masterPull'
import type { ItemAction, ItemField, MachinePlan } from './masterPull'
import { deviceLabel, tagLookup } from './masterView'

// 마스터데이터 [Edge → 명세 가져오기] — 서버를 고르면 그 Edge 의 설비(라인 › 설비)가 나오고, 골라서 명세 인터페이스로 넣는다 (현장 우선, masterPull).
// 반영은 한 번에(되돌리기 한 번). 읽기만 하므로 모니터링 중이어도 된다 — Edge 가 온라인이면 된다.
// 미리보기는 디바이스 [Edge → 명세 가져오기](CompareView)와 같은 규칙 — 기준은 명세: 늘 명세 값을 보여 주고,
// 빨강 = 가져오면 바뀌는 칸(마우스를 올리면 Edge 값) · 초록 = 새로 추가되는 항목 · 회색 "명세에만" = 그대로 남는 항목,
// 주황 = Edge 태그의 디바이스가 명세에 없음 (태그 연결은 바꾸지 않는다). 항목 설명은 가져오지 않는다 (명세 전용 — 디바이스 태그 설명처럼) —
// 항목에서 바뀔 수 있는 건 태그 연결(디바이스·주소 → Alias)뿐.

export default function MasterPullDialog({ machines, devices, onApply, onClose }: {
  machines: SpecMachine[]
  devices: DeviceSpecDevice[]
  onApply: (machines: SpecMachine[], summary: string) => void
  onClose: () => void
}) {
  const [serverId, setServerId] = useState('')
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [previewCode, setPreviewCode] = useState<string | null>(null)

  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const serversQuery = useQuery({ queryKey: ['servers'], queryFn: () => serversApi.getAll().then(r => r.data) })
  const servers = (serversQuery.data ?? []).filter(s => s.status === 'Registered')
  const linesQuery = useQuery({
    queryKey: ['servers', serverId, 'machines'],
    queryFn: () => serversApi.getMachines(serverId).then(r => r.data),
    enabled: !!serverId, retry: false, staleTime: 0,
  })
  // 항목 태그 → 명세 디바이스·주소 를 찾으려고 Edge 디바이스(태그 id)도 받는다
  const devicesQuery = useQuery({
    queryKey: ['servers', serverId, 'devices'],
    queryFn: () => serversApi.getDevices(serverId).then(r => r.data),
    enabled: !!serverId, retry: false, staleTime: 0,
  })
  const loading = linesQuery.isFetching || devicesQuery.isFetching
  const error = linesQuery.error ?? devicesQuery.error

  const rows = useMemo(() => {
    const resolve = tagResolver(devicesQuery.data ?? [], devices)
    return (linesQuery.data ?? []).flatMap(line => line.machines.map(m => {
      const items = pulledItems(m, resolve)
      const spec = machines.find(x => x.code.trim() === m.code)
      return { line: line.name || line.code, machine: m, items, spec, plan: machinePlan(m, items, spec) }
    }))
  }, [linesQuery.data, devicesQuery.data, devices, machines])
  const ready = !!linesQuery.data && !!devicesQuery.data
  const selectable = rows.filter(r => r.plan.kind !== 'same')
  const selected = selectable.filter(r => checked.has(r.machine.code))
  // 미리보기 — 누른 설비, 아직 안 눌렀으면 첫 번째로 바뀌는(또는 새로 추가되는) 설비
  const previewKey = previewCode ?? selectable[0]?.machine.code ?? rows[0]?.machine.code ?? null
  const preview = rows.find(r => r.machine.code === previewKey) ?? null

  const pickServer = (id: string) => { setServerId(id); setChecked(new Set()); setPreviewCode(null) }
  const toggle = (code: string) => setChecked(s => {
    const next = new Set(s)
    if (next.has(code)) next.delete(code)
    else next.add(code)
    return next
  })

  const apply = () => {
    if (selected.length === 0 || !ready) return
    let next = [...machines]
    let added = 0, updated = 0
    for (const r of selected) {
      const merged = mergeEdgeMachine(r.machine, r.items, r.spec)
      if (r.spec) { next = next.map(m => (m.key === r.spec!.key ? merged : m)); updated++ }
      else { next = [...next, merged]; added++ }
    }
    onApply(next, `Edge에서 가져왔습니다 (인터페이스 추가 ${added}, 덮어쓰기 ${updated})`)
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onMouseDown={onClose}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-6xl h-[85vh] flex flex-col overflow-hidden" onMouseDown={e => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800">Edge → 명세 가져오기 — 마스터데이터</h3>
          <select value={serverId} onChange={e => pickServer(e.target.value)}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm min-w-[280px]">
            <option value="">{serversQuery.isLoading ? '불러오는 중…' : '서버를 고르세요'}</option>
            {servers.map(s => (
              <option key={s.id} value={s.id} disabled={!s.isOnline}>
                {s.name}{s.company ? ` · ${s.company.name}` : ''}{s.isOnline ? '' : ' (오프라인)'}
              </option>
            ))}
          </select>
          {loading && <span className="text-xs text-gray-400">Edge 에서 받는 중…</span>}
          <span className="ml-auto text-xs text-gray-500">설비코드를 인터페이스 ID로, 같은 전송코드는 태그 연결만 Edge 값으로 · 항목 설명·명세에만 있는 항목은 그대로</span>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <div className="min-h-0 overflow-auto border-r border-gray-100">
            {!serverId ? (
              <p className="p-8 text-center text-sm text-gray-400">서버를 고르면 그 Edge 의 설비가 나옵니다.</p>
            ) : error ? (
              <p className="p-8 text-center text-sm text-red-600">{getErrorMessage(error, '설비 목록을 받지 못했습니다.')}</p>
            ) : !ready ? (
              <p className="p-8 text-center text-sm text-gray-400">Edge 에서 받는 중…</p>
            ) : rows.length === 0 ? (
              <p className="p-8 text-center text-sm text-gray-400">이 Edge 에는 설비가 없습니다.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
                  <tr>
                    <th className="w-10 px-3 py-2">
                      <input type="checkbox" disabled={selectable.length === 0} title="변경 없는 설비는 빼고 고릅니다"
                        checked={selectable.length > 0 && selectable.every(r => checked.has(r.machine.code))}
                        onChange={ev => setChecked(ev.target.checked ? new Set(selectable.map(r => r.machine.code)) : new Set())} />
                    </th>
                    <th className="text-left px-2 py-2">Edge 설비</th>
                    <th className="text-left px-2 py-2">라인</th>
                    <th className="text-left px-2 py-2">가져오면 명세는</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.map(r => (
                    <tr key={r.machine.code} onClick={() => setPreviewCode(r.machine.code)}
                      className={`cursor-pointer align-top ${r.machine.code === previewKey ? 'bg-blue-50/60' : 'hover:bg-gray-50'} ${r.plan.kind === 'same' ? 'opacity-60' : ''}`}>
                      <td className="px-3 py-2.5" onClick={ev => ev.stopPropagation()}>
                        <input type="checkbox" checked={r.plan.kind !== 'same' && checked.has(r.machine.code)}
                          disabled={r.plan.kind === 'same'} onChange={() => toggle(r.machine.code)} />
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="font-medium text-gray-800">{r.machine.name || r.machine.code}</div>
                        <div className="text-xs text-gray-500 font-mono">{r.machine.code} · 전송코드 {r.items.length}</div>
                      </td>
                      <td className="px-2 py-2.5 text-xs text-gray-600">{r.line || '-'}</td>
                      <td className="px-2 py-2.5 text-xs"><PlanBadge plan={r.plan} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="min-h-0 overflow-auto">
            {preview ? <ItemPreview key={preview.machine.code} machine={preview.machine} items={preview.items} spec={preview.spec} devices={devices} />
              : <p className="p-8 text-center text-sm text-gray-400">설비를 누르면 가져올 전송코드를 미리 볼 수 있습니다.</p>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-t border-gray-100 shrink-0">
          <span className="text-xs text-gray-500">{selected.length > 0 ? `${selected.length}개 선택` : '가져올 설비를 고르세요'}</span>
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition">취소</button>
            <button onClick={apply} disabled={!ready || selected.length === 0}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
              명세에 반영{selected.length ? ` (${selected.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// 설비 목록의 "가져오면 명세는" 칸 — 디바이스 가져오기의 PlanBadge 와 같은 색·말
function PlanBadge({ plan }: { plan: MachinePlan }) {
  if (plan.kind === 'create') return <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700">명세에 새로 추가</span>
  if (plan.kind === 'same') return <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">변경 없음</span>
  const parts = [
    plan.renamed && <span key="n" className="text-red-600">이름·설명</span>,
    plan.change > 0 && <span key="c" className="text-red-600">태그 연결 {plan.change}개 바뀜</span>,
    plan.add > 0 && <span key="a" className="text-green-700">추가 {plan.add}</span>,
    plan.specOnly > 0 && <span key="k" className="text-gray-500">명세에만 {plan.specOnly} 남김</span>,
  ].filter(Boolean)
  return (
    <div>
      <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700">바뀜</span>
      {parts.length > 0 && <div className="mt-1 text-[11px] flex flex-wrap gap-x-1.5">{parts}</div>}
    </div>
  )
}

const CHANGED_CELL = 'bg-red-50 text-red-700 font-medium cursor-help'
const ACTION_LABEL: Partial<Record<ItemAction, { text: string; className: string }>> = {
  add: { text: '추가', className: 'text-green-700' },
  keep: { text: '명세에만', className: 'text-gray-500' },
}
// Alias = 연결한 명세 태그의 Alias (태그 연결이 바뀌면 같이 바뀐다)
const ITEM_COLUMNS: { key: 'code' | ItemField | 'alias'; label: string; mono?: boolean }[] = [
  { key: 'code', label: '전송코드', mono: true },
  { key: 'description', label: '설명' },
  { key: 'device', label: '디바이스' },
  { key: 'address', label: '태그 주소', mono: true },
  { key: 'alias', label: 'Alias', mono: true },
]

// 가져올 전송코드 — 명세 기준 비교 (가져온 뒤 순서: 명세 항목 → 새 항목)
function ItemPreview({ machine, items, spec, devices }: {
  machine: EdgeMachine
  items: ReturnType<typeof pulledItems>
  spec: SpecMachine | undefined
  devices: DeviceSpecDevice[]
}) {
  const [onlyChanged, setOnlyChanged] = useState(false)
  const { tipProps, tipNode, hideTip } = useHoverTip()
  const rows = useMemo(() => compareItems(items, spec), [items, spec])
  const shown = onlyChanged ? rows.filter(x => x.action === 'change' || x.action === 'add' || x.edgeDeviceMissing) : rows
  const deviceName = (key: string) => { const d = devices.find(x => x.key === key); return d ? deviceLabel(d) : '' }
  const lookup = useMemo(() => tagLookup(devices), [devices])
  const aliasOf = (device: string, address: string) => (device && address.trim() ? lookup(device, address)?.tag.alias ?? '' : '')
  const count = (a: ItemAction) => rows.filter(x => x.action === a).length
  const missing = rows.filter(x => x.edgeDeviceMissing).length
  const both = !!spec

  const edgeTip = (value: string): TipContent => ({ title: 'Edge 의 지금 값', value, note: '가져오면 이 값으로 바뀝니다' })
  const missingTip: TipContent = {
    title: '명세에 없는 디바이스',
    note: 'Edge 태그의 디바이스가 명세 디바이스 탭에 없습니다 (디바이스 코드로 찾음). 새 항목은 태그 미연결로, 있던 항목은 지금 연결 그대로 들어갑니다. 디바이스 탭에서 먼저 가져오면 연결됩니다.',
  }

  // 인터페이스 칸 — 가져온 뒤 값은 mergeEdgeMachine 과 같은 labels (Edge 값, 비었으면 명세 값)
  const after = labels(machine, spec)
  const head = [
    { label: '인터페이스 ID', value: machine.code, edge: machine.code, mono: true },
    { label: '이름', value: spec ? spec.name ?? '' : after.name, edge: after.name },
    { label: '설명', value: spec ? spec.description : after.description, edge: after.description },
  ]

  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-100 sticky top-0 bg-surface z-10">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-800">{machine.name || machine.code}</span>
          <span className="font-mono text-xs text-gray-500">{machine.code}</span>
          {!both && <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded bg-green-50 text-green-700">명세에 새로 추가</span>}
        </div>
        <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
          {head.map(h => {
            const changed = both && h.value !== h.edge
            return (
              <div key={h.label} className="contents">
                <dt className="text-gray-500 py-0.5">{h.label}</dt>
                <dd {...tipProps(changed ? edgeTip(h.edge) : undefined)}
                  className={`px-1.5 py-0.5 rounded break-all ${h.mono ? 'font-mono' : ''} ${changed ? CHANGED_CELL : 'text-gray-700'}`}>
                  {h.value || <span className={changed ? 'text-red-300' : 'text-gray-400'}>(비어 있음)</span>}
                </dd>
              </div>
            )
          })}
        </dl>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          <span className="text-gray-500">전송코드 {rows.length}</span>
          {count('change') > 0 && <span className="text-red-600">바뀜 {count('change')}</span>}
          {both && count('add') > 0 && <span className="text-green-700">추가 {count('add')}</span>}
          {count('keep') > 0 && <span className="text-gray-500">명세에만 {count('keep')} (남김)</span>}
          {missing > 0 && <span className="text-orange-600">명세에 없는 디바이스 {missing}</span>}
          {both && (
            <label className="ml-auto flex items-center gap-1.5 text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={onlyChanged} onChange={e => setOnlyChanged(e.target.checked)} />
              달라진 것만 보기
            </label>
          )}
        </div>
        {missing > 0 && (
          <div className="mt-2 text-[11px] text-orange-700 bg-orange-50 rounded px-2 py-1">
            Edge 태그의 디바이스가 명세에 없는 항목이 {missing}개 있습니다. 디바이스 탭에서 [Edge → 명세 가져오기]를 먼저 하면 태그가 연결됩니다.
          </div>
        )}
      </div>

      <table className="w-full text-xs" onMouseLeave={hideTip}>
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="w-1 p-0" />
            <th className="text-left px-2 py-2 w-8">#</th>
            {ITEM_COLUMNS.map(c => <th key={c.key} className="text-left px-2 py-2">{c.label}</th>)}
            {both && <th className="w-16" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {shown.map(x => {
            const added = x.action === 'add' && both
            const bar = x.action === 'change' ? 'bg-red-400' : added ? 'bg-green-400' : ''
            const label = both ? ACTION_LABEL[x.action] : undefined
            return (
              <tr key={x.key} className={added ? 'bg-green-50/40' : ''}>
                <td className={`p-0 ${bar}`} />
                <td className="px-2 py-1.5 text-gray-400 font-mono">{rows.indexOf(x) + 1}</td>
                {ITEM_COLUMNS.map(c => {
                  const aliasNow = aliasOf(x.spec.device, x.spec.address)
                  const aliasAfter = x.edge && x.changed.length ? aliasOf(x.edge.device, x.edge.address) : aliasNow
                  const isChanged = c.key === 'device' || c.key === 'address' ? x.changed.includes(c.key) : c.key === 'alias' && aliasAfter !== aliasNow
                  const edgeValue = !x.edge ? '' : c.key === 'device' ? deviceName(x.edge.device) : c.key === 'address' ? x.edge.address : c.key === 'alias' ? aliasAfter : ''
                  const value = c.key === 'code' ? x.code : c.key === 'device' ? deviceName(x.spec.device) : c.key === 'alias' ? aliasNow : x.spec[c.key]
                  const deviceMissing = c.key === 'device' && x.edgeDeviceMissing
                  const tip = isChanged ? edgeTip(edgeValue) : deviceMissing ? missingTip : undefined
                  return (
                    <td key={c.key} {...tipProps(tip)}
                      className={`px-2 py-1.5 ${c.mono ? 'font-mono' : ''} ${isChanged ? CHANGED_CELL : x.action === 'keep' ? 'text-gray-500' : 'text-gray-700'} ${tip ? 'cursor-help' : ''}`}>
                      {value || (isChanged ? <span className="text-red-300">(비어 있음)</span> : c.key === 'device' && !deviceMissing ? <span className="text-gray-400">미연결</span> : '')}
                      {deviceMissing && <span className="ml-1.5 align-middle font-sans text-[10px] px-1 rounded border border-orange-300 text-orange-600 bg-orange-50">명세에 없음</span>}
                    </td>
                  )
                })}
                {both && <td className={`px-2 py-1.5 text-[11px] ${label?.className ?? ''}`}>{label?.text}</td>}
              </tr>
            )
          })}
          {shown.length === 0 && (
            <tr><td colSpan={ITEM_COLUMNS.length + 3} className="p-6 text-center text-gray-500">{onlyChanged ? '달라진 항목이 없습니다.' : '전송코드가 없습니다.'}</td></tr>
          )}
        </tbody>
      </table>
      {tipNode}
    </div>
  )
}
